import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { logout, redirectForSSOLogin, useAuthCheckFailed } from '@onesource/services';
import { MdLogout, MdRefresh } from 'react-icons/md';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.15); }
  50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
`;

export const AuthCheckFailedDialog = () => {
  const failed = useAuthCheckFailed();

  if (!failed) {
    return null;
  }

  return (
    <Backdrop
      open
      sx={{
        zIndex: theme => theme.zIndex.modal + 2,
        bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.7)'),
        backdropFilter: 'blur(12px)',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 420,
          width: '90%',
          p: { xs: 3.5, sm: 5 },
          borderRadius: 4,
          textAlign: 'center',
          animation: `${fadeIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
          border: theme => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${alpha('#EF4444', 0.1)} 0%, ${alpha('#F97316', 0.06)} 100%)`,
            border: `1px solid ${alpha('#EF4444', 0.15)}`,
            mx: 'auto',
            mb: 3,
            animation: `${float} 3s ease-in-out infinite, ${pulse} 3s ease-in-out infinite`,
          }}
        >
          <Box component="svg" viewBox="0 0 24 24" fill="none" sx={{ width: 34, height: 34, color: '#EF4444' }}>
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 1, letterSpacing: '-0.01em' }}>
          Unable to connect
        </Typography>

        {/* Body */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.6, maxWidth: 320, mx: 'auto' }}>
          We couldn&apos;t verify your session. This can happen on a slow or unstable connection. Please refresh the
          page to try again.
        </Typography>

        {/* Action */}
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button
            variant="soft"
            size="large"
            startIcon={<MdLogout />}
            onClick={() => {
              // Fire-and-forget logout, then redirect to SSO login.
              logout().catch(() => undefined);
              redirectForSSOLogin();
            }}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 2.5,
              fontWeight: 600,
              fontSize: '0.9375rem',
              textTransform: 'none',
            }}
          >
            Log Out
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<MdRefresh />}
            onClick={() => window.location.reload()}
            sx={theme => ({
              px: 4,
              py: 1.25,
              borderRadius: 2.5,
              fontWeight: 600,
              fontSize: '0.9375rem',
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            })}
          >
            Refresh Page
          </Button>
        </Stack>

        {/* Subtle hint */}
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
          If this keeps happening, try clearing your browser cache.
        </Typography>
      </Paper>
    </Backdrop>
  );
};
